
import { Component, Vue, Prop } from 'vue-property-decorator';
import OrgErrorMessage from '@/components/OrgErrorMessage.vue';
import { CameraChildErrorCode } from '@/resources/error/CameraChildErrorCode';
import { ShootType } from '@/store/modules/camera';
import IdDocType from '@/resources/enum/IdDocType';
import { IDDocPassiveErrorCode, extractDocPassiveErrorCode } from '@/util/cameraErrorUtils';
import { is, isPassport } from '@/util/idDoc';

type AutomaticCode1 = 'frame' | 'size' | 'sharpness' | 'highlight' | 'brightness';
type AutomaticCode2 =
  | 'confidence'
  | 'diagonal'
  | 'is_grayscale'
  | 'similarity'
  | 'face'
  | 'straight'
  | 'face_quality'
  | 'qrcode';
// 容貌真贋判定
type AutomaticCode3 =
  | 'i_close'
  | 'i_cropped'
  | 'i_close_to_border'
  | 'i_small'
  | 'i_eyes_closed'
  | 'i_occluded'
  | 'i_many_faces'
  | 'i_angle_too_large'
  | 'i_low_quality'
  | 'i_undefined';

type ErrorCode = CameraChildErrorCode | AutomaticCode1 | AutomaticCode2 | AutomaticCode3 | IDDocPassiveErrorCode;

@Component({
  components: {
    OrgErrorMessage
  }
})
export default class Parts extends Vue {
  private messages1: string[] = [];
  private dotList: string[][] = [];
  private checkList: { text: string; checked: boolean }[] = [];
  private messages2: string[] = [];
  private isManyRetry: boolean = false;

  @Prop({ type: Array, required: true })
  private errorFields!: string[];

  private get isIdDocFront(): boolean {
    return this.$store.state.camera.shootType === 'identity-document-front';
  }

  private get isIdDocDiagonal(): boolean {
    return this.$store.state.camera.shootType === 'identity-document-diagonal';
  }

  private get isIdDocBack(): boolean {
    return this.$store.state.camera.shootType === 'identity-document-back';
  }

  private get isFaceFront(): boolean {
    return this.$store.state.camera.shootType === 'face-front';
  }

  private get isFaceAction(): boolean {
    return this.$store.state.camera.shootType === 'face-action';
  }

  private get idDocType(): IdDocType {
    const storeIdDocType: IdDocType = this.$store.state.camera.idDocType;

    // エラー画面の動作確認用に、書類選択前ならクエリパラメータ好きな書類を指定できる
    if (storeIdDocType === IdDocType.Unknown) {
      const queryIdDocType: string | (string | null)[] | undefined = this.$route.query.iddoctype;
      const numberIdDocType: number = typeof queryIdDocType === 'string' ? Number.parseInt(queryIdDocType, 10) : NaN;
      if (!Number.isNaN(numberIdDocType)) return numberIdDocType;
    }

    return storeIdDocType;
  }

  private get isIdDocMyNumberCard(): boolean {
    if (is(this.idDocType, 'my-number-card')) return true;

    return false;
  }

  private get isIdDocResidenceCard(): boolean {
    if (is(this.idDocType, 'residence-card')) return true;

    return false;
  }

  private get isIdDocSpecialPermanentResidentCertificate(): boolean {
    if (is(this.idDocType, 'special-permanent-resident-certificate')) return true;

    return false;
  }

  private get isIdDocPassport(): boolean {
    if (isPassport(this.idDocType)) return true;

    return false;
  }

  /**
   * 本人確認書類画像登録APIの結果に対して表示するエラーメッセージの項目を返す
   */
  private get automaticErrorMessageItems(): AutomaticCode1[] {
    if (this.isIdDocFront || this.isIdDocBack) {
      // 在留カードはhighlight未対応のため返さない
      if (this.isIdDocResidenceCard) {
        return ['frame', 'size', 'sharpness', 'brightness'];
      } else {
        return ['frame', 'size', 'sharpness', 'highlight', 'brightness'];
      }
    }
    if (this.isIdDocDiagonal) return ['sharpness'];
    if (this.isFaceFront) return ['sharpness', 'brightness', 'highlight'];
    return [];
  }

  private get docPassiveErrorCode(): IDDocPassiveErrorCode | null {
    return extractDocPassiveErrorCode(this.errorFields);
  }

  public async mounted(): Promise<void> {
    const shootType: ShootType = this.$store.state.camera.shootType;

    const isPC: boolean = this.$store.getters['camera/isPC'];

    if (this.errorFields.indexOf(CameraChildErrorCode.ShootTimeout) >= 0) {
      // 手動撮影タイムアウトエラー
      const key1: string = 'cameraCameraChildError.messages.ShootTimeout';
      this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
      this.dotList = [];
      this.checkList = [];
      this.messages2 = [];
      this.isManyRetry = false;
    } else if (this.errorFields.indexOf(CameraChildErrorCode.AutodetectTimeout) >= 0) {
      // 自動撮影タイムアウトエラー
      const key1: string = 'cameraCameraChildError.messages.AutodetectTimeout';
      this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
      this.dotList = [];
      this.checkList = [];
      this.messages2 = [];
      this.isManyRetry = false;
    } else if (this.errorFields.indexOf(CameraChildErrorCode.Automatic) >= 0) {
      // 撮影結果の自動判定エラー処理
      if (this.errorFields.indexOf(CameraChildErrorCode.ManyRetry) >= 0) {
        const keySection: string = this.isFaceFront || this.isFaceAction ? 'face' : 'idDoc';
        const key1: string = `cameraCameraChildError.messages.manyRetry.${keySection}.messages1`;
        const key2: string = `cameraCameraChildError.messages.manyRetry.${keySection}.dotList`;
        const orgName: string = this.$store.getters['auth/currentLanguageOrgName'];
        this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
        this.dotList = this.$t(key2) as { [key: number]: { [key: number]: string } } as string[][];
        this.checkList = [];
        this.messages2 = [];
        this.isManyRetry = true;
      } else if (this.errorFields.indexOf('is_grayscale') >= 0) {
        // 白黒撮影エラー
        const key1: string = 'cameraCameraChildError.messages.is_grayscale';
        this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
        this.dotList = [];
        this.checkList = [];
        this.messages2 = [];
        this.isManyRetry = false;
      } else if (this.errorFields.indexOf('confidence') >= 0) {
        // 信頼性エラー
        const key1: string = 'cameraCameraChildError.messages.confidence.messages1';
        const key2: string = 'cameraCameraChildError.messages.confidence.dotList';
        const key3: string = 'cameraCameraChildError.messages.confidence.messages2';
        this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
        this.dotList = this.$t(key2) as { [key: number]: { [key: number]: string } } as string[][];
        this.checkList = [];
        this.messages2 = this.$t(key3) as { [key: number]: string } as string[];
        this.isManyRetry = false;
      } else if (this.errorFields.indexOf('diagonal') >= 0) {
        // 斜め撮影エラー
        const key1: string = 'cameraCameraChildError.messages.diagonal.messages1';
        const key2: string = 'cameraCameraChildError.messages.diagonal.dotList';
        const key3: string = 'cameraCameraChildError.messages.diagonal.messages2';
        this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
        this.dotList = this.$t(key2) as { [key: number]: { [key: number]: string } } as string[][];
        this.checkList = [];
        this.messages2 = this.$t(key3) as { [key: number]: string } as string[];
        this.isManyRetry = false;
      } else if (this.errorFields.indexOf('face_quality') >= 0) {
        const key1: string = 'cameraCameraChildError.messages.face_quality';
        this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
        this.dotList = [];
        this.checkList = [];
        this.messages2 = [];
        this.isManyRetry = false;
      } else if (this.errorFields.indexOf('similarity') >= 0) {
        // 写真同一性エラー
        const isPassportDiagonal = this.isIdDocPassport && this.isIdDocDiagonal;
        const partSection = isPassportDiagonal ? `passport.${shootType}` : 'other';
        const key1: string = `cameraCameraChildError.messages.similarity.${partSection}`;
        this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
        this.dotList = [];
        this.checkList = [];
        this.messages2 = [];
        this.isManyRetry = false;
      } else if (this.errorFields.indexOf('face') >= 0) {
        // 顔認識エラー
        const key1: string = 'cameraCameraChildError.messages.face.messages';
        const key2: string = 'cameraCameraChildError.messages.face.dotList';
        this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
        this.dotList = this.$t(key2) as { [key: number]: { [key: number]: string } } as string[][];
        this.checkList = [];
        this.messages2 = [];
        this.isManyRetry = false;
      } else if (this.errorFields.indexOf('qrcode') >= 0) {
        // マイナンバーカード裏面のQRコードを取得できない
        const key1: string = 'cameraCameraChildError.messages.qrcode.messages';
        this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
        this.dotList = [];
        this.checkList = [];
        this.messages2 = [];
        this.isManyRetry = false;
      } else if (this.isFaceFront && this.errorFields.indexOf('frame') >= 0) {
        // 顔正面撮影の枠内判定
        const key1: string = 'cameraCameraChildError.messages.straight.messages1';
        const key2: string = 'cameraCameraChildError.messages.straight.dotList';
        this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
        this.dotList = this.$t(key2) as { [key: number]: { [key: number]: string } } as string[][];
        this.checkList = [];
        this.messages2 = [];
        this.isManyRetry = false;
      } else if (this.isFaceFront && this.errorFields.indexOf('straight') >= 0) {
        // 顔正面撮影の正面判定
        const key1: string = 'cameraCameraChildError.messages.straight.messages1';
        const key2: string = 'cameraCameraChildError.messages.straight.dotList';
        this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
        this.dotList = this.$t(key2) as { [key: number]: { [key: number]: string } } as string[][];
        this.checkList = [];
        this.messages2 = [];
        this.isManyRetry = false;
      } else if (
        this.errorFields.indexOf('frame') >= 0 ||
        this.errorFields.indexOf('size') >= 0 ||
        this.errorFields.indexOf('sharpness') >= 0 ||
        this.errorFields.indexOf('highlight') >= 0 ||
        this.errorFields.indexOf('brightness') >= 0
      ) {
        // 色々な撮影エラー
        const key1: string = 'cameraCameraChildError.messages.Automatic.messages';
        this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
        this.dotList = [];
        this.messages2 = [];
        this.isManyRetry = false;
        this.checkList = this.automaticErrorMessageItems.map(
          (
            code: AutomaticCode1
          ): {
            text: string;
            checked: boolean;
          } => ({
            text: this.$t(`cameraCameraChildError.messages.Automatic.${shootType}.${code}`) as string,
            checked: this.errorFields.some((field: string): boolean => field === code)
          })
        );
      } else if (this.errorFields.indexOf('i_close') >= 0) {
        // 顔がカメラに近すぎる
        const key1: string = 'cameraCameraChildError.messages.i_close.messages';
        this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
        this.dotList = [];
        this.checkList = [];
        this.messages2 = [];
        this.isManyRetry = false;
      } else if (this.errorFields.indexOf('i_cropped') >= 0) {
        // 顔がずれている
        const key1: string = 'cameraCameraChildError.messages.i_cropped.messages';
        this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
        this.dotList = [];
        this.checkList = [];
        this.messages2 = [];
        this.isManyRetry = false;
      } else if (this.errorFields.indexOf('i_close_to_border') >= 0) {
        // 顔がずれている
        const key1: string = 'cameraCameraChildError.messages.i_close_to_border.messages';
        this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
        this.dotList = [];
        this.checkList = [];
        this.messages2 = [];
        this.isManyRetry = false;
      } else if (this.errorFields.indexOf('i_small') >= 0) {
        // 顔が小さすぎる
        const key1: string = 'cameraCameraChildError.messages.i_small.messages';
        this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
        this.dotList = [];
        this.checkList = [];
        this.messages2 = [];
        this.isManyRetry = false;
      } else if (this.errorFields.indexOf('i_eyes_closed') >= 0) {
        // 目を閉じている
        const key1: string = 'cameraCameraChildError.messages.i_eyes_closed.messages';
        this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
        this.dotList = [];
        this.checkList = [];
        this.messages2 = [];
        this.isManyRetry = false;
      } else if (this.errorFields.indexOf('i_occluded') >= 0) {
        // 顔が検出できない
        const key1: string = 'cameraCameraChildError.messages.i_occluded.messages';
        const key2: string = 'cameraCameraChildError.messages.i_occluded.dotList';
        this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
        this.dotList = this.$t(key2) as { [key: number]: { [key: number]: string } } as string[][];
        this.checkList = [];
        this.messages2 = [];
        this.isManyRetry = false;
      } else if (this.errorFields.indexOf('i_many_faces') >= 0) {
        // 検出された顔が多すぎる
        const key1: string = 'cameraCameraChildError.messages.i_many_faces.messages';
        const key2: string = 'cameraCameraChildError.messages.i_many_faces.dotList';
        this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
        this.dotList = this.$t(key2) as { [key: number]: { [key: number]: string } } as string[][];
        this.checkList = [];
        this.messages2 = [];
        this.isManyRetry = false;
      } else if (this.errorFields.indexOf('i_angle_too_large') >= 0) {
        // 顔が正面を向いていない
        const key1: string = 'cameraCameraChildError.messages.i_angle_too_large.messages';
        this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
        this.dotList = [];
        this.checkList = [];
        this.messages2 = [];
        this.isManyRetry = false;
      } else if (this.errorFields.indexOf('i_low_quality') >= 0) {
        // qualityが閾値を下回った場合
        const key1: string = 'cameraCameraChildError.messages.i_low_quality.messages';
        const key2: string = 'cameraCameraChildError.messages.i_low_quality.dotList';
        this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
        this.dotList = this.$t(key2) as { [key: number]: { [key: number]: string } } as string[][];
        this.checkList = [];
        this.messages2 = [];
        this.isManyRetry = false;
      } else if (this.errorFields.indexOf('i_undefined') >= 0) {
        // その他システムエラー
        const key1: string = 'cameraCameraChildError.messages.i_undefined.messages';
        this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
        this.dotList = [];
        this.checkList = [];
        this.messages2 = [];
        this.isManyRetry = false;
        // 書類真贋判定エラー
      } else if (this.docPassiveErrorCode !== null) {
        this.messages1 = this.$t(`cameraCameraChildError.messages.${this.docPassiveErrorCode}.messages`) as {
          [key: number]: string;
        } as string[];
        this.dotList = [];
        this.checkList = [];
        this.messages2 = [];
        this.isManyRetry = false;
      } else {
        // サーバから受け取ったfieldsが空っぽ！！
        if (
          (this.isIdDocResidenceCard || this.isIdDocSpecialPermanentResidentCertificate) &&
          (this.isIdDocFront || this.isIdDocBack)
        ) {
          // 在留カード・特別永住者証明書の表面と裏面の場合は信頼性エラー
          const key1: string = 'cameraCameraChildError.messages.confidence.messages1';
          const key2: string = 'cameraCameraChildError.messages.confidence.dotList';
          const key3: string = 'cameraCameraChildError.messages.confidence.messages2';
          this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
          this.dotList = this.$t(key2) as { [key: number]: { [key: number]: string } } as string[][];
          this.checkList = [];
          this.messages2 = this.$t(key3) as { [key: number]: string } as string[];
          this.isManyRetry = false;
        } else if (
          (this.isIdDocResidenceCard || this.isIdDocSpecialPermanentResidentCertificate) &&
          this.isIdDocDiagonal
        ) {
          // 在留カード・特別永住者証明書の斜面の場合は斜め撮影エラー
          const key1: string = 'cameraCameraChildError.messages.diagonal.messages1';
          const key2: string = 'cameraCameraChildError.messages.diagonal.dotList';
          const key3: string = 'cameraCameraChildError.messages.diagonal.messages2';
          this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
          this.dotList = this.$t(key2) as { [key: number]: { [key: number]: string } } as string[][];
          this.checkList = [];
          this.messages2 = this.$t(key3) as { [key: number]: string } as string[];
          this.isManyRetry = false;
        } else if (isPC && this.isIdDocMyNumberCard && (this.isIdDocFront || this.isIdDocBack)) {
          // PCのマイナンバーカードの表面と裏面の場合は信頼性エラー
          const key1: string = 'cameraCameraChildError.messages.confidence.messages1';
          const key2: string = 'cameraCameraChildError.messages.confidence.dotList';
          const key3: string = 'cameraCameraChildError.messages.confidence.messages2';
          this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
          this.dotList = this.$t(key2) as { [key: number]: { [key: number]: string } } as string[][];
          this.checkList = [];
          this.messages2 = this.$t(key3) as { [key: number]: string } as string[];
          this.isManyRetry = false;
        } else if (isPC && this.isIdDocMyNumberCard && this.isIdDocDiagonal) {
          // PCのマイナンバーカードの斜面の場合は斜め撮影エラー
          const key1: string = 'cameraCameraChildError.messages.diagonal.messages1';
          const key2: string = 'cameraCameraChildError.messages.diagonal.dotList';
          const key3: string = 'cameraCameraChildError.messages.diagonal.messages2';
          this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
          this.dotList = this.$t(key2) as { [key: number]: { [key: number]: string } } as string[][];
          this.checkList = [];
          this.messages2 = this.$t(key3) as { [key: number]: string } as string[];
          this.isManyRetry = false;
        } else if (this.isIdDocFront || this.isIdDocBack) {
          // 表面と裏面の認識失敗エラー
          const key1: string = 'cameraCameraChildError.messages.identity-document-front-back';
          this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
          this.dotList = [];
          this.checkList = [];
          this.messages2 = [];
          this.isManyRetry = false;
        } else if (this.isIdDocDiagonal) {
          // 斜面の認識失敗エラー
          const key1: string = 'cameraCameraChildError.messages.identity-document-diagonal';
          this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
          this.dotList = [];
          this.checkList = [];
          this.messages2 = [];
          this.isManyRetry = false;
        } else if (this.isFaceFront) {
          // 顔正面の認識失敗エラー
          const key1: string = 'cameraCameraChildError.messages.face-front.messages';
          const key2: string = 'cameraCameraChildError.messages.face-front.dotList';
          this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
          this.dotList = this.$t(key2) as { [key: number]: { [key: number]: string } } as string[][];
          this.checkList = [];
          this.messages2 = [];
          this.isManyRetry = false;
        }
      }
    } else {
      // その他不明なエラー
      const key1: string = 'cameraCameraChildError.messages.others';
      this.messages1 = this.$t(key1) as { [key: number]: string } as string[];
      this.dotList = [];
      this.checkList = [];
      this.messages2 = [];
      this.isManyRetry = false;
    }
  }
}
